import { apiErrorMessage, isForbidden } from '@agos/api-client'
import type { OrderListItem } from '@agos/api-client/model'
import { useGetEmployees, useGetEmployeesDrivers } from '@agos/api-client/hr'
import { postFiskalPrintNote, postFiskalReceipt, useGetFiskalRegisters } from '@agos/api-client/fiskal'
import { useGetSettingsGeneral } from '@agos/api-client/platform'
import {
  getGetOrdersQueryKey,
  postOrdersBulkDispatch,
  postOrdersOrderIdConfirmStatus,
  useGetOrders,
  useGetSalesChannels,
  usePatchOrdersOrderId,
} from '@agos/api-client/orders'
import {
  alreadyPrinted,
  ArrivalSoundPicker,
  chimeFor,
  chimeToggleLabel,
  EmployeeAuthModal,
  filterChannelsOf,
  FISKAL_STORNO,
  FiskalStornoAction,
  formatCents,
  formatMoney,
  mayStornoFiskal,
  orderStatusLabel,
  parseMoneyToCents,
  preorderLeadMinutes,
  printVirtualReceipt,
  registerName,
  registerWarningsOf,
  SHIPPED_REGISTER_NAMES,
  startOfDay,
  T,
  tt,
  useArrivalChime,
  useAuth,
  useCounterPayment,
  useFranchiseModules,
  useModalFocus,
  offersToCouriers,
  usesDrivers,
  virtualReceiptOf,
} from '@agos/shared'
import {
  CancelOrderModal,
  BUTTON_GHOST,
  cn,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  StatusBadge,
  toast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  Ban,
  Banknote,
  CalendarClock,
  Check,
  Clock,
  CreditCard,
  Globe,
  MapPin,
  Pencil,
  Phone,
  Printer,
  QrCode,
  Receipt,
  RefreshCw,
  Send,
  ShoppingBag,
  StickyNote,
  Store,
  Truck,
  User,
  UserCheck,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'wouter'

import { mayAssign, orderIdsToWrite, outcomeNote, planFor, planNote } from './bulk-assign'
import { CourierOfferAction, CourierSettleDialog, CourierStateBadge } from './CourierBoard'
import { courierStateOf, externalRuns, isExternalDriver, offerActionFor } from './courier-board'
import {
  askNote,
  assignPayload,
  assignableWaiters,
  closeReasonFor,
  closeReasonFromTheBoard,
  mustAskWhoServed,
  noWaitersNote,
  unattributedNote,
  type WaiterLike,
} from './waiter-assign'

import {
  boardArrivalStep,
  boardBands,
  boardOrders,
  credentialsNeeded,
  customerLabel,
  customerTitle,
  DISPATCH_ORDER,
  dispatchButtonFor,
  dispatchRefusedNote,
  onRoadLabel,
  driverLoads,
  outsideEveryZone,
  paymentApprovalBadge,
  protectedStatusPlan,
  statusChangeConfirmLabel,
  statusChangeSubtitle,
  statusChangeTitle,
  statusRoleNote,
  visibleOrders,
  type BoardOrder,
} from './board'
import {
  batchNeedsAttention,
  batchRegisterReport,
  planAnswered,
  printNotePayload,
  printRegisterPlan,
  receiptPayloadFor,
  receiptOutcomeNote,
  printOutcomeNote,
  registerFor,
  type RegisterPlan,
} from './delivery-note'
import { paysAtTheCounter, takesPaymentHere } from './counter-close'
import {
  ACCEPT_ORDER,
  arrivalLabel,
  canReassignDriver,
  mayRemoveDriver,
  unassignDriverPayload,
  channelBadge,
  discountParts,
  dispatchRuns,
  driverControlFor,
  editButtonFor,
  elapsedLabel,
  isDispatchable,
  mayCloseFromBoard,
  mayOpenOrderEditor,
  nextStatusLabel,
  paymentBadge,
  paymentMethodBadge,
  scheduledBadge,
  sourceBadge,
  transitionsFor,
  verificationOf,
  withoutCompleted,
} from './dispatch-rules'

import { printBagByBag } from './batch-print'
import { driversNotOffered, notOfferedReason } from './drivers-not-offered'

/**
 * `/dispatch` — *Rozvoz — nástenka*, ported from legacy `pages/dispatch.tsx`.
 *
 * The road: everything not yet delivered, who is carrying it, and the one action
 * that matters — sending a driver out with their run.
 *
 * §14.1 — ten seconds, copied. The countdown ring is here for the same reason it
 * is on the kitchen board: a dispatcher who cannot see the tick has no way to
 * tell a quiet evening from a screen that stopped fetching.
 */

const REFRESH_SECONDS = 10
const ORDERS_PAGE_SIZE = '500'

const TYPE_ICONS: Record<string, typeof Truck> = {
  delivery: Truck,
  restaurant: Store,
  bar: ShoppingBag,
}

const STATUS_TABS = [
  // First, as the owner asked — §9.9. `active` stays selected on open, because that is the working
  // view; this is the tab you reach for when an order has gone somewhere you cannot see.
  { id: 'all', label: tt('Všetko') },
  { id: 'active', label: tt('Aktívne') },
  { id: 'pending', label: tt('Čaká na prijatie') },
  { id: 'new', label: tt('Nová') },
  { id: 'preparing', label: tt('Príprava') },
  { id: 'quality_check', label: tt('Kontrola kvality') },
  { id: 'on_the_way', label: tt('Na ceste') },
  { id: 'delivered', label: tt('Doručená') },
] as const

interface DriverLike {
  id?: number
  name?: string
  /** §9.786 - an external courier, who settles up at *Vyraziť*. The drivers read has always carried it. */
  isExternal?: boolean | null
}

/**
 * §9.149 — legacy's *"left strip color and row background tint per status"*, ported from
 * `dispatch.tsx`'s own `STATUS_STRIP` and `STATUS_TINT`.
 *
 * **Local to this board on purpose.** `manager-orders` has a badge palette for the same statuses and
 * it disagrees — `on_the_way` is violet there and near-black here — because they are different
 * pictures: a badge names a state, a strip lets an eye landing mid-list see which row it is on. A
 * shared map would have to pick one and be wrong on the other screen.
 */
const STATUS_STRIP: Record<string, string> = {
  pending: 'bg-red-500',
  new: 'bg-green-500',
  preparing: 'bg-violet-500',
  ready: 'bg-orange-500',
  quality_check: 'bg-blue-500',
  on_the_way: 'bg-zinc-900',
  at_address: 'bg-orange-500',
  delivered: 'bg-emerald-500',
  cancelled: 'bg-zinc-400',
}

const STATUS_TINT: Record<string, string> = {
  pending: 'bg-red-500/5',
  new: 'bg-green-500/5',
  preparing: 'bg-violet-500/5',
  ready: 'bg-orange-500/5',
  quality_check: 'bg-blue-500/5',
  on_the_way: 'bg-zinc-900/5',
  at_address: 'bg-orange-500/5',
  delivered: 'bg-emerald-500/5',
  cancelled: 'bg-zinc-400/5',
}

/** A status this board has never heard of keeps the plain card rather than losing the row. */
function statusStrip(status: string | undefined): string {
  return STATUS_STRIP[status ?? ''] ?? 'bg-transparent'
}

function statusTint(status: string | undefined): string {
  return STATUS_TINT[status ?? ''] ?? 'bg-card'
}

export function DispatchPage() {
  const queryClient = useQueryClient()
  const [, navigate] = useLocation()
  const { hasPermission, user } = useAuth()
  /* §9.728, owner O98 - *Vyraziť* is the right `dispatch_order` (BACKEND.md §10A.376), not the board's. Read once,
     so the selection bar and every card answer the same question. */
  const mayDispatch = hasPermission(DISPATCH_ORDER)
  /* §9.744, owner O114 - *✓ Prijať* and *Upraviť* on their own rights (BACKEND.md §10A.397) rather than on the
     board's `change_order_status` - *Upraviť* on `edit_order` and `new_order`, the editor's own route (§9.744
     amended). Read once, as `mayDispatch` is, so every card answers the same question. */
  const mayAccept = hasPermission(ACCEPT_ORDER)
  const mayEditOrder = mayOpenOrderEditor(hasPermission)
  /* §9.786, owner O139 - external couriers. *Ponúknuť externým* is the board's own right (the endpoint asks
     `dispatch`) and needs the module; the settling dialog opens after *Vyraziť* with an external driver, one run at
     a time. */
  const { isModuleEnabled } = useFranchiseModules()
  const couriers = isModuleEnabled('external_driver')
  const mayOffer = hasPermission('dispatch')
  const [settlingRuns, setSettlingRuns] = useState<{ driverId: number; orderIds: readonly number[] }[]>([])

  const [filterStatus, setFilterStatus] = useState<string>('active')
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set())
  const [secondsToRefresh, setSecondsToRefresh] = useState(REFRESH_SECONDS)
  const [isDispatching, setIsDispatching] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [isPrintingFiskal, setIsPrintingFiskal] = useState(false)
  const chime = useArrivalChime()
  const [bulkDriverId, setBulkDriverId] = useState<number | null>(null)
  /* §9.701, owner O85 - polled, because `isAvailable` is read off this list for the line naming who drives and
     cannot be sent: a list as old as the page would call somebody who has just ended their shift "not seen for four
     hours". A minute rather than the board's own refresh - the list is every employee, 22 fields each. */
  const { data: employeeData } = useGetEmployees({ query: { refetchInterval: 60_000 } })
  const waiters = useMemo(
    () => assignableWaiters(Array.isArray(employeeData) ? (employeeData as WaiterLike[]) : []),
    [employeeData],
  )
  const [isAssigning, setIsAssigning] = useState(false)
  const [settling, setSettling] = useState<{ order: BoardOrder; status: string } | null>(null)
  /**
   * §9.442, owner question #39 - the selection's reversals, one at a time.
   *
   * **The passwords are not skippable and that is the server's design**, not an omission here:
   * `POST /fiskal/storno` re-authenticates whoever authorises each reversal because *"the person at
   * the till is not necessarily the person entitled to reverse a registered sale - and the reversal
   * carries their name"*. So a batch is a **queue** rather than one call: the modal asks for the
   * head, and on success the head is dropped and the next one asked. The count is in the button and
   * in the progress line, so nobody reverses three sales believing they reversed one.
   */
  const [stornoQueue, setStornoQueue] = useState<BoardOrder[]>([])
  const [askingWho, setAskingWho] = useState<BoardOrder | null>(null)
  /* §9.740, owner O111 - the till's *Zaplatiť*, which the shell hands over (§5: this module cannot import it). Null
     when nothing was provided, and then *Vydaná* closes the order the way it always has. The row is kept as it was
     when the dialog opened, and the fresh one read while it is still on the board. */
  const CounterPayment = useCounterPayment()
  const [paying, setPaying] = useState<OrderListItem | null>(null)
  /* §9.740, owner O110 - a batch print waiting for its registers: the plan, the answers so far and what to run
     with them. */
  const [registerQuestion, setRegisterQuestion] = useState<{
    plan: RegisterPlan
    answers: Record<string, string>
    run: (registerOf: (order: BoardOrder) => string) => Promise<void>
  } | null>(null)
  /* §9.488, `TODO.md` 3c - the trap and the Escape. Unconditional: this picker writes only when a waiter is
     chosen, so dismissing it before that loses nothing. */
  const whoPanelRef = useRef<HTMLElement>(null)
  const { onKeyDown: whoKeyDown } = useModalFocus(true, whoPanelRef, () => setAskingWho(null))
  const [now, setNow] = useState(() => new Date())
  const seenOrderIds = useRef<Set<number> | null>(null)

  const today = startOfDay(new Date()).toISOString()
  const { data: result, isLoading } = useGetOrders(
    { date_from: today, page_size: ORDERS_PAGE_SIZE },
    { query: { refetchInterval: REFRESH_SECONDS * 1000 } },
  )
  const allOrders = useMemo(() => (result?.data ?? []) as BoardOrder[], [result])
  /* §9.740 - the row the dialog is paying, as fresh as the board's last read. */
  const payingNow = useMemo(() => {
    if (paying === null) return null
    return (result?.data ?? []).find((row) => row.id === paying.id) ?? paying
  }, [paying, result])
  /* §9.740 - the registers' names for the question below. A read some board accounts are refused (#141), so
     `registerName` falls back to the shipped names and then to the id itself, rather than to nothing. */
  const { data: registerData } = useGetFiskalRegisters()
  const registerRows = useMemo(
    () =>
      Array.isArray(registerData)
        ? (registerData as Array<{ kasaId?: string | null; label?: string | null }>)
        : [],
    [registerData],
  )

  const { data: driverData } = useGetEmployeesDrivers({
    query: { refetchInterval: REFRESH_SECONDS * 1000 },
  })
  const drivers = useMemo(
    () => (Array.isArray(driverData) ? (driverData as DriverLike[]) : []),
    [driverData],
  )

  const patchOrder = usePatchOrdersOrderId()

  /**
   * The ring and the clock, one tick. It does not fetch — FB-046 again: the
   * query owns its interval, and a second invalidation on the same schedule
   * would double the request rate.
   */
  useEffect(() => {
    const tick = setInterval(() => {
      setNow(new Date())
      setSecondsToRefresh((remaining) => (remaining <= 1 ? REFRESH_SECONDS : remaining - 1))
    }, 1000)
    return () => clearInterval(tick)
  }, [])

  // §9.5: the board asks what a channel does. Required rather than optional, so it cannot be forgotten.
  const { data: salesChannels } = useGetSalesChannels()
  const channels = useMemo(() => filterChannelsOf(salesChannels), [salesChannels])
  /* §9.740 - **and the dialog goes when the order stops being one to pay here**: closed on another screen, paid
     online in the meantime, gone from the board. Left open it would take a second payment and close the order again
     over whatever the other screen did. **Derived rather than cleared in an effect**: the dialog is drawn only while
     this is false, and the next press replaces the order it was for. Said, because a dialog vanishing under
     somebody's hand is otherwise a fault. */
  const payingGone = useMemo(() => {
    if (paying === null) return false
    const fresh = (result?.data ?? []).find((row) => row.id === paying.id)
    return fresh === undefined || !paysAtTheCounter(fresh, channels)
  }, [paying, result, channels])
  useEffect(() => {
    if (!payingGone) return
    toast({
      title: tt('Objednávka sa medzitým zmenila'),
      description: tt('Bola uzavretá alebo zaplatená inde, preto sa zaplatenie zavrelo.'),
    })
  }, [payingGone])
  /* §9.643, owner O34 - the same setting the kitchen reads, so the two screens agree about when a preorder
     starts being work. `preorderLeadMinutes` fills in the 60-minute default when the franchise has set none. */
  const { data: generalSettings } = useGetSettingsGeneral()
  const leadMinutes = preorderLeadMinutes(generalSettings)
  const onBoard = useMemo(
    () => boardOrders(allOrders, channels, now, leadMinutes),
    [allOrders, channels, now, leadMinutes],
  )
  const shown = useMemo(
    () => visibleOrders(allOrders, filterStatus, channels, now, leadMinutes),
    [allOrders, filterStatus, channels, now, leadMinutes],
  )
  const loads = useMemo(() => driverLoads(onBoard), [onBoard])
  /* §9.701, owner O85, FB-1007 - *"Even tho I have 'Jazdí aj rozvoz' - i dont see them in rozvoz - nástenka"*. The
     picker offers whoever drives, is on shift and was seen in four hours, for a marked account exactly as for a
     driver (BACKEND.md §10A.358); this names the marked accounts it cannot offer and why. Off the picker's own answer,
     so it cannot contradict the select; `undefined` when the employee read is refused draws nothing. */
  const notOffered = useMemo(
    () => driversNotOffered(Array.isArray(employeeData) ? employeeData : undefined, drivers),
    [employeeData, drivers],
  )

  /** A web order nobody has looked at is announced differently from an accepted one. */
  useEffect(() => {
    /* FB-997, §9.677 - `result` rather than the list's length: an empty morning is an answer, and remembering it is
       what lets the day's first order be announced. */
    const step = boardArrivalStep(result === undefined ? undefined : allOrders, seenOrderIds.current)
    seenOrderIds.current = step.seen
    const notice = step.notice

    // §9.110 — the same arrival that raises the toast makes the sound, so nothing fires on a
    // re-render and the urgent pattern goes to the order nobody has accepted.
    const kind = chimeFor({ pending: notice.pending.length, accepted: notice.accepted.length })
    if (kind !== null) chime.play(kind)

    if (notice.pending.length > 0) {
      toast({
        title: `⚠️ ${notice.pending.length === 1 ? tt('Nová objednávka z webu!') : `${notice.pending.length} ${tt('nové objednávky z webu!')}`}`,
        description: notice.pending
          .map((order) => `#${order.orderNumber} - ${formatMoney(order.total)}`)
          .join('  |  '),
      })
    }
    if (notice.accepted.length > 0) {
      toast({
        title: `🔔 ${notice.accepted.length === 1 ? tt('Nová objednávka') : `${notice.accepted.length} ${tt('nové objednávky')}`}!`,
        description: notice.accepted
          .map((order) => `#${order.orderNumber} - ${formatMoney(order.total)}`)
          .join('  |  '),
      })
    }
  }, [result, allOrders, chime])

  /**
   * One slip per selected order — §9.108.
   *
   * <p>Sequential, and a failure does not stop the rest: the register is a single serial device, and
   * the orders that can be printed should be. Each failure is named in the toast rather than folded
   * into a count, because a dispatcher needs to know *which* driver is about to leave without one.
   */
  const printNotesForSelected = async (registerOf: (order: BoardOrder) => string) => {
    if (selectedOrders.length === 0) return
    setIsPrinting(true)
    let printed = 0
    const failed: string[] = []
    // §9.417, FB-744 - **what the register said.** This loop had a bare `catch {}`, so the owner
    // read *Vytlačené: 0 · nevytlačené: #15903* three times and it never told them why. The first
    // refusal is kept: a batch fails for one reason far more often than for eight.
    let reason = ''
    // §9.515 - which orders finished, so the rows that printed drop out of the selection and the
    // ones that did not stay ticked for a second attempt.
    const printedIds: number[] = []
    for (const order of selectedOrders) {
      try {
        await postFiskalPrintNote(printNotePayload(order, registerOf(order)))
        printed += 1
        printedIds.push(order.id ?? 0)
      } catch (caught) {
        failed.push(`#${order.orderNumber ?? order.id ?? '?'}`)
        if (reason === '') {
          reason = apiErrorMessage(caught, tt('tlačiareň neodpovedala'))
        }
      }
    }
    setIsPrinting(false)
    setSelected((current) => withoutCompleted(current, printedIds))
    toast({
      ...(failed.length === 0 ? {} : { variant: 'destructive' as const }),
      title: printOutcomeNote(printed, failed, reason),
    })
  }

  /**
   * §9.123 — the bulk fiscal print legacy has on this bar and AGOS did not.
   *
   * **Sequential, one order at a time.** A register is a single serial device and firing eight
   * requests at it in parallel is how two documents interleave. **A failure stops nothing else** —
   * one unreachable register must not hold up the other seven — and every failure is named by order
   * number, because an unissued receipt is a sale with no tax document and somebody has to chase it.
   *
   * The delivery notes follow **only the orders that printed**: a note for a bag whose receipt failed
   * would send a driver out with an undocumented sale.
   */
  const printFiskalForSelected = async (registerOf: (order: BoardOrder) => string) => {
    if (selectedOrders.length === 0) return
    setIsPrintingFiskal(true)
    // §9.500 - what each print said about the register, collapsed after the batch.
    const registerReports: { warning: string | null; paperOut: boolean }[] = []

    /* §9.472, owner report R2 - **bag by bag, not document by document.**
     *
     * This was two loops: every receipt, then every slip. On a single serial register that emits
     * `fiskal(1), fiskal(2), fiskal(3), slip(1), slip(2), slip(3)`, which is the owner's own sentence -
     * *"now, we are printing fiskal, order 1, order2, then sprievodny"* - and it leaves a dispatcher
     * pairing sixteen sheets across eight bags by reading order numbers.
     *
     * `printBagByBag` holds the sequence so that a test can see it; every rule the two loops were
     * written for is in its javadoc and unchanged. The four outcome lists §9.417 and §9.362 separated
     * come back exactly as before, and the toast below is untouched. */
    const outcome = await printBagByBag(selectedOrders, {
      // §9.740 - the register the plan chose for this order: `rozvoz` unless its channel names its own.
      payloadFor: (order) => receiptPayloadFor(order, registerOf(order)),
      /* §9.498 - **the register does not always print it, and then the browser must.** A receipt sent
       * while the register is unreachable comes back `virtualReceipt: true` with a 200 and no paper, so
       * awaiting this call and moving on printed the slip and no document at all. `virtualReceiptOf`
       * is the shared reader of that shape; the till has done this since §11.4. */
      receipt: async (payload) => {
        const answer = await postFiskalReceipt(payload)
        /* §9.500 - **the register's own report, which this effect used to throw away.** `paperOut` after a
         * successful close is an `ok` outcome by design: the document is registered and reprints once paper
         * is inserted. So a batch could say *Vytlačené: 8* with nothing on the tray. Collected per order and
         * said once below, because eight identical toasts is the failure it exists to prevent. */
        registerReports.push(registerWarningsOf(answer))
        const virtual = virtualReceiptOf(answer)
        if (virtual !== null && !printVirtualReceipt(virtual, Date.now())) {
          toast({
            variant: 'destructive',
            title: tt('Doklad sa nepodarilo otvoriť'),
            description: tt('Povoľte vyskakovacie okná a doklad vytlačte znova.'),
          })
        }
        return answer
      },
      alreadyPrinted,
      // The slip comes out where the receipt did, so one bag's two sheets come out of one device.
      note: (order) => postFiskalPrintNote(printNotePayload(order, registerOf(order))),
      /* BACKEND.md §10A.379 - the register's own sentence, said once below. A register left inactive answers
       * *Pokladňa nie je aktivovaná (Kasa Rozvoz)*, and this batch used to show only the order numbers. */
      reasonOf: (caught) => apiErrorMessage(caught, tt('kasa neodpovedala')),
    })

    setIsPrintingFiskal(false)
    /* §9.515 - `issued` **and** `alreadyHad`: an order that already carried a document needs nothing
       further, so it drops out too. `failed` and `refused` stay ticked, which is the whole reason this
       is not a blanket clear. */
    setSelected((current) =>
      withoutCompleted(current, [...outcome.issued, ...outcome.alreadyHad].map((one) => one.id ?? 0)),
    )
    const label = (order: (typeof selectedOrders)[number]) =>
      `#${order.orderNumber ?? order.id ?? '?'}`
    toast({
      // §9.417 - a refusal is a problem too, so it carries the same colour as a failure. It used to
      // be counted in `failed`, which coloured the toast correctly by accident.
      //
      // §9.499 - **and a batch that issued nothing is not a success either.** Every order already
      // having a document is the owner's own report: the slip prints, the receipt does not, and this
      // used to be styled as a success while a sheet came out of the printer. `alreadyHad` on its own
      // stays quiet, because then some receipt did print and the rest are genuinely nothing to chase.
      ...(batchNeedsAttention({
        issued: outcome.issued.length,
        failed: outcome.failed.length,
        refused: outcome.refused.length,
        alreadyHad: outcome.alreadyHad.length,
      })
        ? { variant: 'destructive' as const }
        : {}),
      title: receiptOutcomeNote(
        outcome.issued.length,
        outcome.failed.map(label),
        outcome.alreadyHad.map(label),
        outcome.refused.map(label),
        outcome.reason,
      ),
    })

    /* §9.500 - after the outcome, because it is about the device rather than the batch. The paper case is
     * destructive: the documents are registered and none of them is on the tray. */
    const register = batchRegisterReport(registerReports)
    if (register.paperOut > 0) {
      toast({
        variant: 'destructive',
        title: tt('Papier chýba v kase'),
        description: `${tt('Doklady boli uzavreté a dotlačia sa po vložení papiera. Počet:')} ${register.paperOut}`,
      })
    }
    for (const warning of register.warnings) {
      toast({ title: tt('Upozornenie kasy'), description: warning })
    }
  }

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
    setSecondsToRefresh(REFRESH_SECONDS)
  }

  const toggleSelected = (orderId: number) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(orderId)) next.delete(orderId)
      else next.add(orderId)
      return next
    })

  const selectedOrders = shown.filter((order) => selected.has(order.id ?? 0))
  /**
   * The ones in the selection a storno can actually reach - §9.442.
   *
   * Derived from the same rule the per-order button uses, so the toolbar's count and the cards'
   * buttons can never disagree about which orders are reversible.
   */
  const stornoableSelection = useMemo(
    () => selectedOrders.filter((order) => mayStornoFiskal(order)),
    [selectedOrders],
  )
  const runs = dispatchRuns(selectedOrders)
  const bulkPlan = bulkDriverId === null ? null : planFor(selectedOrders, bulkDriverId)
  const bulkDriverName =
    drivers.find((driver) => driver.id === bulkDriverId)?.name ?? tt('šofér')

  /**
   * N patches, because assignment is per order — `bulk-dispatch` moves already-assigned orders and is a
   * different action. A failure does not stop the rest: the bags that can go, go.
   */
  const assignSelected = async () => {
    if (bulkPlan === null || !mayAssign(bulkPlan) || bulkDriverId === null) return
    setIsAssigning(true)
    let written = 0
    let failed = 0
    let assignReason = ''
    // §9.515, FB-800 - this is the action where leaving rows ticked cost something: `patchOrder` does
    // not refuse a no-op, so pressing *Priradit* twice was a second audited edit on every order.
    const writtenIds: number[] = []
    for (const orderId of orderIdsToWrite(bulkPlan)) {
      try {
        await patchOrder.mutateAsync({ orderId, data: { driverId: bulkDriverId } })
        written += 1
        writtenIds.push(orderId)
      } catch (caught) {
        failed += 1
        // §9.417 - the same bare `catch` one function along. *"3 failed"* with no reason is the
        // same defect as the slip toast, on the action that decides who drives where.
        if (assignReason === '') {
          assignReason = apiErrorMessage(caught, tt('server neodpovedal'))
        }
      }
    }
    setIsAssigning(false)
    setSelected((current) => withoutCompleted(current, writtenIds))
    refresh()
    toast({
      ...(failed === 0 ? {} : { variant: 'destructive' as const }),
      title: outcomeNote(written, failed),
      // §9.417 - the reason, where a toast has a place for one. `outcomeNote`'s own signature is
      // shared with two other callers, so the cause goes in the description rather than widening it.
      ...(assignReason === '' ? {} : { description: assignReason }),
    })
  }

  const assignDriver = (order: BoardOrder, driverId: number) => {
    if (!canReassignDriver(order)) {
      // §14.5 — the credit belongs to the journey somebody is making.
      toast({
        variant: 'destructive',
        title: tt('Šofér je na ceste'),
        description: tt('Objednávku musí vrátiť šofér cez mobilnú aplikáciu.'),
      })
      return
    }
    patchOrder.mutate(
      { orderId: order.id ?? 0, data: { driverId } },
      {
        onSuccess: refresh,
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Priradenie zlyhalo'),
            description: apiErrorMessage(caught, ''),
          }),
      },
    )
  }

  /** §9.812, owner O155 - *Odstrániť*: the server's existing unassign, refused by it once the order has left. */
  const removeDriver = (order: BoardOrder) => {
    if (!canReassignDriver(order)) {
      toast({
        variant: 'destructive',
        title: tt('Šofér je na ceste'),
        description: tt('Objednávku musí vrátiť šofér cez mobilnú aplikáciu.'),
      })
      return
    }
    patchOrder.mutate(
      { orderId: order.id ?? 0, data: unassignDriverPayload() },
      {
        onSuccess: () => {
          refresh()
          toast({ title: tt('Šofér odstránený'), description: tt('Objednávka je opäť bez šoféra.') })
        },
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Odstránenie šoféra zlyhalo'),
            description: apiErrorMessage(caught, ''),
          }),
      },
    )
  }

  /**
   * Run a batch print once every order knows its register - `FRONTEND.md` §9.740, owner O110.
   *
   * **Most batches ask nothing**: a delivery and a channel that names no register print on `rozvoz`, and a channel
   * naming one prints there (`printRegisterPlan`). Only a channel naming two or more is asked about, once for all
   * its selected orders, and the batch waits for the answer rather than guessing which device takes the money.
   */
  const withRegisters = (run: (registerOf: (order: BoardOrder) => string) => Promise<void>) => {
    if (selectedOrders.length === 0) return
    const plan = printRegisterPlan(selectedOrders, channels)
    if (plan.ask.length === 0) {
      void run((order) => registerFor(order, plan, {}))
      return
    }
    setRegisterQuestion({ plan, answers: {}, run })
  }

  const changeStatus = (order: BoardOrder, status: string) => {
    /* §9.740, owner O111 - *Vydaná* on a counter pickup takes the payment: the till's *Zaplatiť*, which prints the
       receipt if none is out yet and closes the order as *Doručená*, owned by whoever pressed it (the dialog names
       nobody, so the server's session rule does - BACKEND.md §10A.386). Only for somebody the till's close admits,
       because the dialog prints before it closes; anybody else, an order paid online, and a build without the
       slot keep the close below. */
    if (
      status === 'delivered' &&
      CounterPayment !== null &&
      paysAtTheCounter(order, channels) &&
      takesPaymentHere(user?.systemRole, hasPermission)
    ) {
      const row = (result?.data ?? []).find((one) => one.id === order.id)
      if (row !== undefined) {
        setPaying(row)
        return
      }
    }
    // FB-075: a bar bill nobody is on goes into the takings and into no wallet. The one moment somebody
    // knows who served it is now, while it is being closed.
    // §9.391 - the channel decides who owns the money, so `na_sekeru` is asked too.
    // §9.740, owner O106 - **not an Osobný odber any more**: the server credits whoever closes it
    // (BACKEND.md §10A.386), so the press below closes it and the person pressing *Vydaná* owns it.
    if (status === 'delivered' && mustAskWhoServed(order, channels)) {
      setAskingWho(order)
      return
    }

    // §9.341, FB-547 - the owner: *"when I want to storno some orders, I rredceive error … Použite
    // /confirm-status"*. `OrderUpdateService` refuses all three settling statuses on the ordinary
    // PATCH, so the board could not storno at all and printed the server's developer-facing guard
    // into a toast.
    //
    // **No credentials, and no modal.** `OrderActorResolver.resolve` falls back to the signed-in
    // principal when e-mail and password are absent, so the actor, the role gate and the status log
    // are all the same as they would be behind a prompt - and asking a signed-in administrator for
    // the password they signed in with proves nothing the token has already proved.
    if (protectedStatusPlan(status) === 'confirm') {
      /* §9.576, FB-876 - **a close is not a storno and must not ask like one.** The owner: *"when
       * using «Vydaná» button, its getting us to storno, so we cant vydať it."* `CancelOrderModal`
       * prints *Storno sa zaznamená na vaše meno* beside a red triangle and refuses to submit until a
       * reason is typed, so the routine one of the three settling statuses was the one that could not
       * be completed.
       *
       * **`delivered` closes with the board's own reason**, which is what the waiter path five
       * hundred lines below has always done (`closeReasonFor`) and what legacy does for all three
       * with no modal at all. Everybody who can open this board may set it - `ROLES_FOR.delivered` is
       * `{main_admin, admin, waiter, driver}` - so the dialog was collecting nothing but a sentence
       * nobody had anything to put in.
       *
       * **The other two keep it, and so does a `delivered` this session cannot authorise.** A storno
       * reverses money and stock, and a waiter needs a manager for both of those, so there the dialog
       * is §9.341's *manager walks over* flow rather than a prompt for its own sake. */
      if (status === 'delivered' && !credentialsNeeded(user?.systemRole ?? '', status, hasPermission)) {
        void closeFromTheBoard(order)
        return
      }
      // The reason is collected for the other two: `reason` is `@NotBlank`, and for a storno or a
      // return to the kitchen it is the one thing only the person doing it knows.
      setSettling({ order, status })
      return
    }

    patchOrder.mutate(
      { orderId: order.id ?? 0, data: { status } },
      {
        onSuccess: refresh,
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Nepodarilo sa zmeniť stav.'),
            description: apiErrorMessage(caught, ''),
          }),
      },
    )
  }

  /**
   * One request per driver, because a driver leaving with three bags is one
   * journey. Failures are reported per run and the others still go — a dispatcher
   * mid-rush needs the drivers that *can* leave to leave.
   */
  /**
   * A *Vydaná* or *Doručená* that needs nothing asked - §9.576, FB-876.
   *
   * Reports both ways, because the failure modes differ and neither may be silent: a **403** is said
   * as who *may* close it, since the server's own sentence for the PATCH guard names an endpoint, and
   * anything else is said verbatim. The toast names the reason that went into the status log, exactly
   * as the dialog's own success toast did, so the trail is visible without opening the order.
   */
  const closeFromTheBoard = async (order: BoardOrder) => {
    const reason = closeReasonFromTheBoard()
    try {
      await postOrdersOrderIdConfirmStatus(order.id ?? 0, { newStatus: 'delivered', reason })
      refresh()
      toast({ title: tt('Objednávka je uzavretá'), description: `${tt('Dôvod')}: ${reason}` })
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Objednávku sa nepodarilo uzavrieť.'),
        description: isForbidden(caught) ? statusRoleNote('delivered') : apiErrorMessage(caught, ''),
      })
    }
  }

  /** §9.341 - the order and status waiting on a reason (and maybe an administrator). */
  const confirmSettling = async (email: string, password: string, reason: string) => {
    if (settling === null) return
    const { order, status } = settling
    try {
      await postOrdersOrderIdConfirmStatus(order.id ?? 0, {
        newStatus: status,
        reason,
        // Sent only when collected. **Empty strings would be worse than absent**:
        // `OrderActorResolver.resolve` checks `email != null && password != null`, so a blank pair
        // would fall to `byCredentials` and fail on a password nobody typed, instead of falling back
        // to the signed-in principal.
        ...(email.trim() === '' ? {} : { email: email.trim(), password }),
      })
      setSettling(null)
      refresh()
      toast({ title: tt('Stav zmenený'), description: `${tt('Dôvod')}: ${reason}` })
    } catch (caught) {
      // Rethrown so the modal shows it in place and keeps what was typed - and a 403 is said as who
      // *may* do it, because the server's own sentence for the PATCH guard names an endpoint.
      throw new Error(
        isForbidden(caught) ? statusRoleNote(status) : apiErrorMessage(caught, ''),
        { cause: caught },
      )
    }
  }

  /**
   * Send one order, through the same endpoint the bar's *Vyraziť* uses - §9.417, report 5.
   *
   * **Not a status change.** `NEXT_STATUSES` has no `on_the_way` target: an order leaves through
   * `bulk-dispatch`, which is what makes the journey, the mileage and the wallet credit land on the
   * right driver. A per-row button that patched the status would have moved the order and created no
   * journey.
   */
  const dispatchOne = async (order: BoardOrder) => {
    if (!isDispatchable(order) || typeof order.driverId !== 'number') return
    setIsDispatching(true)
    try {
      await postOrdersBulkDispatch({ orderIds: [order.id ?? 0], driverId: order.driverId })
      toast({ title: `${tt('Vyrazená objednávka')} #${order.orderNumber ?? order.id ?? '?'}` })
      // §9.786 - an external courier settles up as he leaves.
      if (isExternalDriver(drivers, order.driverId) || courierStateOf(order) === 'claimed') {
        setSettlingRuns([{ driverId: order.driverId, orderIds: [order.id ?? 0] }])
      }
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Chyba dispatchu'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa odoslať objednávku.')),
      })
    }
    setIsDispatching(false)
    refresh()
  }

  const dispatchSelected = async () => {
    if (runs.length === 0) {
      toast({
        variant: 'destructive',
        title: tt('Žiadne vhodné objednávky'),
        description: tt(
          'Vybrané objednávky musia byť v stave Kontrola kvality s priradeným šoférom.',
        ),
      })
      return
    }

    setIsDispatching(true)
    let sent = 0
    // §9.515 - per run rather than all-or-nothing. A run that threw leaves its orders ticked, so a
    // dispatcher can send that driver's bags again without re-ticking the ones that already went.
    const sentIds: number[] = []
    const sentRuns: { driverId: number; orderIds: readonly number[] }[] = []
    for (const run of runs) {
      try {
        // `dispatched` is typed now, so neither the cast nor the runtime check earns its place.
        const response = await postOrdersBulkDispatch({
          orderIds: run.orderIds,
          driverId: run.driverId,
        })
        sent += response.dispatched
        sentIds.push(...run.orderIds)
        sentRuns.push(run)
      } catch (caught) {
        toast({
          variant: 'destructive',
          title: tt('Chyba dispatchu'),
          description: apiErrorMessage(caught, tt('Nepodarilo sa odoslať objednávky.')),
        })
      }
    }

    refresh()
    setIsDispatching(false)
    /* §9.515 - outside the `sent > 0` block, and that is the change: it used to clear **everything**
       whenever anything went, so one failed run took its own rows off the board's selection along with
       the successful ones. Now each run answers for its own orders. */
    setSelected((current) => withoutCompleted(current, sentIds))
    // §9.786 - each external courier who just left settles up, one after another.
    setSettlingRuns(
      externalRuns(
        sentRuns,
        drivers,
        selectedOrders.filter((order) => courierStateOf(order) === 'claimed').map((order) => order.id ?? 0),
      ),
    )
    if (sent > 0) {
      const eligible = selectedOrders.filter(isDispatchable).length
      toast({
        title: `${tt('Vyrazené')} (${sent}/${eligible})`,
        description: tt('Objednávky odoslané na cestu.'),
      })
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Rozvoz - nástenka')}
        icon={<Truck className="w-5 h-5" />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="text-[11px] text-muted-foreground tabular-nums"
              title={tt('Obnovuje sa automaticky každých 10s.')}
              aria-live="polite"
            >
              {secondsToRefresh}s
            </span>
            {/* §9.110 - off by default, and the press is also the gesture the browser needs before
              * an AudioContext will start. Legacy has this on the same toolbar. */}
            <button
              type="button"
              onClick={chime.toggle}
              aria-label={chimeToggleLabel(chime.enabled)}
              data-sound-toggle={chime.enabled ? 'on' : 'off'}
              className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
            >
              {chime.enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            {/* §9.676, owner O68 - *"rozvoz nástenka zmeniť zvuk príchodu novej obj"*: which sound an accepted
              * arrival makes on this device. The urgent beep for an unaccepted web order is not a choice. */}
            <ArrivalSoundPicker chime={chime} className={cn(BUTTON_GHOST, 'p-2 rounded-lg')} />
            <button
              type="button"
              onClick={refresh}
              aria-label={tt('Obnoviť')}
              className={cn(BUTTON_GHOST, 'p-2 rounded-lg')}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        }
      />

      {loads.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {loads.map((load) => (
            <div
              key={load.driverId}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-violet-500/10 border border-violet-500/25 text-xs"
            >
              <User className="w-3.5 h-3.5 text-violet-500" />
              <span className="font-bold">{load.name}</span>
              <span className="text-muted-foreground">
                {load.orderCount} · {formatCents(load.totalCents)}
              </span>
            </div>
          ))}
        </div>
      )}

      {notOffered.length > 0 && (
        <p className="text-[11px] text-muted-foreground" data-drivers-not-offered>
          <T>Jazdia aj rozvoz, ale teraz sa nedajú priradiť:</T>{' '}
          {notOffered.map((one, index) => (
            <span key={one.id} data-driver-not-offered={one.reason}>
              {index > 0 && ', '}
              <span className="font-semibold text-foreground">{one.name}</span> ({notOfferedReason(one.reason)})
            </span>
          ))}
        </p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {STATUS_TABS.map((tab) => {
          // `all` and `active` are not statuses, so counting rows whose status equals the tab id gives
          // them nothing — *Všetko* would have shown up empty beside six tabs with numbers.
          const count =
            tab.id === 'all'
              ? onBoard.length
              : tab.id === 'active'
                ? visibleOrders(allOrders, 'active', channels, now, leadMinutes).length
                : onBoard.filter((order) => order.status === tab.id).length
          return (
            <button
              key={tab.id}
              type="button"
              data-status-tab={tab.id}
              onClick={() => setFilterStatus(tab.id)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-bold transition-colors',
                filterStatus === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/70 text-muted-foreground',
              )}
            >
              {tab.label} {count > 0 && <span className="tabular-nums">({count})</span>}
            </button>
          )
        })}
      </div>

      {/* §9.150 - **always visible, as legacy's is.** It rendered only once something was selected,
        * so a freshly loaded board showed filter chips and nothing else: every action this screen
        * exists for was invisible until you had already done the one thing whose control was also
        * invisible. Each button is disabled on an empty selection and says so, which is §9.148's
        * rule — an affordance that explains its refusal beats one that is absent. */}
      <div
        className="flex flex-wrap items-center gap-2 px-3 py-2.5 rounded-xl bg-card border border-border"
        data-dispatch-actions
      >
          <span className="text-xs font-bold">
            {selected.size > 0
              ? `${selected.size} ${tt('vybraných')}`
              : tt('Vyberte objednávky')}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {selected.size === 0
              ? tt('označte riadky vľavo')
              : runs.length > 0
                ? `${runs.length} ${runs.length === 1 ? tt('jazda') : tt('jazdy')}`
                : tt('nič na vyrazenie')}
          </span>
          {bulkPlan !== null && (
            <span className="text-[11px] text-muted-foreground" data-bulk-plan>
              {planNote(bulkPlan, bulkDriverName)}
            </span>
          )}
          <div className="flex-1" />
          {/* §9.154 - legacy's *Vybrať všetky*, which we had dropped. Printing forty delivery notes
            * is the flow this bar exists for, and forty clicks to get there is not that flow. It
            * selects what is on screen, not what exists: a filtered board selects the filter. */}
          <button
            type="button"
            data-select-all
            onClick={() =>
              setSelected((current) =>
                current.size === shown.length && shown.length > 0
                  ? new Set()
                  : new Set(shown.map((order) => order.id).filter((id): id is number => id !== undefined)),
              )
            }
            disabled={shown.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="w-3.5 h-3.5" />
            {selected.size === shown.length && shown.length > 0 ? (
              <T>Zrušiť všetky</T>
            ) : (
              <T>Vybrať všetky</T>
            )}
          </button>
          {/* §9.123 - legacy's order, left to right: print the slips, print the receipts, then
            * assign, then dispatch, with the cancel pushed to the far right where it cannot be hit
            * by accident. */}
          <button
            type="button"
            onClick={() => withRegisters(printNotesForSelected)}
            disabled={isPrinting || selectedOrders.length === 0}
            data-print-notes
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Printer className="w-3.5 h-3.5" /> <T>Tlačiť sprievodný list</T>
          </button>
          <button
            type="button"
            onClick={() => withRegisters(printFiskalForSelected)}
            disabled={isPrintingFiskal || selectedOrders.length === 0}
            data-print-fiskal
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Receipt className="w-3.5 h-3.5" />{' '}
            {isPrintingFiskal ? <T>Odosielam…</T> : <T>Tlačiť fiškálny doklad</T>}
          </button>
          {/* §9.442, FB-761 - *"next to Tlačit fiškálny doklad"*, and the owner chose **both**
            * shapes (question #39). The count is on the label deliberately: the print beside it is
            * routine and a reversal is not, so *Storno fiškál (3)* is the one thing that stops
            * somebody reversing three registered sales believing they reversed one. Hidden entirely
            * when nothing in the selection has a reversible document. */}
          {stornoableSelection.length > 0 && (
            <button
              type="button"
              data-storno-selected={String(stornoableSelection.length)}
              onClick={() => setStornoQueue(stornoableSelection)}
              disabled={stornoQueue.length > 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-500 text-xs font-bold hover:bg-red-500/20 transition-colors disabled:opacity-50"
            >
              <Ban className="w-3.5 h-3.5" /> <T>Storno fiškál</T> ({stornoableSelection.length})
            </button>
          )}
          {/* §9.442 - how many passwords are left. A batch is one prompt per reversal by the
            * endpoint's design, so without this the second prompt reads as the screen having
            * ignored the first. */}
          {stornoQueue.length > 0 && (
            <span className="text-xs font-bold text-red-500" data-storno-progress>
              {stornoQueue.length} <T>zostáva stornovať</T>
            </span>
          )}
          {/* Assign the selection to one driver - three bags going the same way, named once. */}
          <select
            value={bulkDriverId === null ? '' : String(bulkDriverId)}
            onChange={(event) =>
              setBulkDriverId(event.target.value === '' ? null : Number(event.target.value))
            }
            aria-label={tt('Priradiť šoférovi')}
            data-bulk-driver
            className="min-h-[32px] rounded-lg bg-card border border-border px-2 text-xs"
          >
            <option value="">{tt('- šofér -')}</option>
            {drivers.map((driver) => (
              <option key={driver.id} value={driver.id}>
                {driver.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-bulk-assign
            onClick={() => void assignSelected()}
            disabled={isAssigning || bulkPlan === null || !mayAssign(bulkPlan)}
            className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold hover:bg-muted transition-colors disabled:opacity-50"
          >
            <T>Priradiť</T>
          </button>
          <button
            type="button"
            data-dispatch-all
            onClick={() => void dispatchSelected()}
            disabled={isDispatching || runs.length === 0 || !mayDispatch}
            title={mayDispatch ? undefined : dispatchRefusedNote()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-3.5 h-3.5" /> <T>Vyraziť</T>
          </button>
          {/* §9.728 - said on the bar as well as in the tooltip: a tablet has no hover, and a driver pressing a dead
            * button needs to know which right to ask for. Only while there is something to send. */}
          {!mayDispatch && runs.length > 0 && (
            <span className="text-[11px] font-semibold text-amber-500" data-dispatch-refused>
              {dispatchRefusedNote()}
            </span>
          )}
          {/* Last, and away from the actions - legacy puts it here for the same reason. */}
          <button
            type="button"
            data-clear-selection
            onClick={() => setSelected(new Set())}
            className="px-2.5 py-1.5 rounded-lg border border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <T>Zrušiť výber</T>
          </button>
        </div>

      {unattributedNote(shown, channels) !== '' && (
        <p className="text-[11px] text-amber-500 px-1" data-unattributed>
          {unattributedNote(shown, channels)}
        </p>
      )}

      {/* §9.341, FB-547 - the board had no modal at all, so a storno hit the server's PATCH guard
        * and printed *"Použite /confirm-status"* to the dispatcher. The reason is always asked; the
        * credentials only when this session cannot authorise the change. */}
      {/* §9.442 - the selection's reversals, asked one at a time. The head is dropped on success
        * and the next asked, so a batch of three is three passwords and three documents, which is
        * what the endpoint does and what the tax office sees. */}
      {stornoQueue[0] !== undefined && (
        <EmployeeAuthModal
          meta={{
            orderId: stornoQueue[0].id ?? 0,
            orderNumber: stornoQueue[0].orderNumber ?? '',
            orderTotalCents: parseMoneyToCents(stornoQueue[0].total ?? 0),
            newStatus: FISKAL_STORNO,
          }}
          onClose={() => setStornoQueue([])}
          onSuccess={() => {
            // §9.515 - the reversal that just succeeded drops out of the selection as well as out of
            // the queue, so what stays ticked is what still has a document standing.
            const done = stornoQueue[0]?.id ?? 0
            setSelected((current) => withoutCompleted(current, [done]))
            setStornoQueue((queue) => queue.slice(1))
            refresh()
          }}
        />
      )}

      {/* §9.786, owner O139 - *settled at pickup, netted*: one amount per external courier who just left. */}
      {settlingRuns[0] !== undefined && (
        <CourierSettleDialog
          key={`${String(settlingRuns[0].driverId)}-${settlingRuns[0].orderIds.join(',')}`}
          driverId={settlingRuns[0].driverId}
          driverName={
            drivers.find((driver) => driver.id === settlingRuns[0]?.driverId)?.name ??
            `${tt('Šofér')} #${String(settlingRuns[0].driverId)}`
          }
          orderIds={settlingRuns[0].orderIds}
          onClose={() => {
            setSettlingRuns((queue) => queue.slice(1))
            refresh()
          }}
        />
      )}

      {settling !== null && (
        <div data-settling={settling.status}>
          <CancelOrderModal
            order={settling.order}
            askCredentials={credentialsNeeded(user?.systemRole ?? '', settling.status, hasPermission)}
            subtitle={statusChangeSubtitle(settling.order, settling.status)}
            // §9.409 - the heading and the button say which of the three this is. The subtitle has
            // named it since §9.341; these two were hard-coded to the cancel wording, so pressing
            // *Vydaná* opened a dialog headed *Stornovať objednávku*.
            title={statusChangeTitle(settling.status)}
            confirmLabel={statusChangeConfirmLabel(settling.status)}
            onClose={() => setSettling(null)}
            onConfirm={confirmSettling}
          />
        </div>
      )}

      {/* §9.740, owner O111 - *Vydaná* on a counter pickup: the till's own *Zaplatiť*, from the shell's slot. */}
      {payingNow !== null && !payingGone && CounterPayment !== null && (
        <div data-counter-payment={payingNow.id}>
          {/* The slot holds one module-level component the shell provides once (`CounterPaymentDialog`), so its
              identity never changes between renders - which is what the rule below guards against. */}
          {/* eslint-disable-next-line react-hooks/static-components */}
          <CounterPayment order={payingNow} onClose={() => setPaying(null)} onPaid={refresh} />
        </div>
      )}

      {/* §9.740, owner O110 - which register a channel that names more than one prints this batch on. Asked once
          per channel, and nothing prints until every channel has an answer. */}
      {registerQuestion !== null && (
        <ConfirmDialog
          open
          title={tt('Na ktorej kase tlačiť?')}
          description={tt('Tento kanál má na výber viac kás. Vyberte, na ktorej sa doklady a sprievodné listy vytlačia.')}
          confirmLabel={tt('Tlačiť')}
          confirmDisabled={!planAnswered(registerQuestion.plan, registerQuestion.answers)}
          onClose={() => setRegisterQuestion(null)}
          onConfirm={() => {
            const { plan, answers, run } = registerQuestion
            if (!planAnswered(plan, answers)) return
            setRegisterQuestion(null)
            void run((order) => registerFor(order, plan, answers))
          }}
        >
          <div className="space-y-3" data-print-register-question>
            {registerQuestion.plan.ask.map((question) => (
              <label key={question.slug} className="block">
                <span className="text-xs font-semibold">
                  {question.name} · {question.orderIds.length}
                </span>
                <select
                  value={registerQuestion.answers[question.slug] ?? ''}
                  onChange={(event) =>
                    setRegisterQuestion((current) =>
                      current === null
                        ? null
                        : { ...current, answers: { ...current.answers, [question.slug]: event.target.value } },
                    )
                  }
                  aria-label={`${tt('Kasa')} - ${question.name}`}
                  data-print-register={question.slug}
                  className="mt-1 w-full min-h-[40px] rounded-xl bg-card border border-border px-2 text-sm"
                >
                  <option value="" disabled>
                    {tt('- vyberte kasu -')}
                  </option>
                  {question.registers.map((kasaId) => (
                    <option key={kasaId} value={kasaId}>
                      {registerName(kasaId, registerRows, SHIPPED_REGISTER_NAMES)}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </ConfirmDialog>
      )}

      {askingWho !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-who-served>
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label={tt('Zavrieť')}
            onClick={() => setAskingWho(null)}
          />
          {/* §9.488 - the role is on the `<section>`, because the wrapper holds the dismiss scrim. The
              waiter list comes from a query this board refreshes, and that doubt is answered by the hook's
              own javadoc: the tab stops are read **at the keypress**, not captured on open, so a list that
              changes under the poll is the case it was built for. */}
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="who-served-title"
            ref={whoPanelRef}
            onKeyDown={whoKeyDown}
            className="relative w-full max-w-sm rounded-2xl bg-background border border-border p-5 space-y-3"
          >
            <h2 id="who-served-title" className="text-sm font-bold">
              <T>Kto obsluhoval?</T>
            </h2>
            <p className="text-[11px] text-muted-foreground" data-who-served-note>
              {askNote(askingWho)}
            </p>
            {waiters.length === 0 ? (
              <p className="text-[11px] text-amber-500" data-no-waiters>
                {noWaitersNote()}
              </p>
            ) : (
              <div className="space-y-1.5">
                {waiters.map((waiter) => (
                  <button
                    key={waiter.id}
                    type="button"
                    data-pick-waiter={waiter.id}
                    onClick={() => {
                      const target = askingWho
                      setAskingWho(null)
                      // §9.342 - two writes, and the second used to be the plain `PATCH` with a
                      // settling status: the call `OrderUpdateService.guard` refuses, with **no
                      // `onError`**. So the waiter was assigned, the close failed, and nothing was
                      // said. It goes through `confirm-status` now, like every other settling status
                      // on this board since §9.341, and both halves report.
                      void (async () => {
                        try {
                          await patchOrder.mutateAsync({
                            orderId: target.id ?? 0,
                            data: assignPayload(waiter.id ?? 0),
                          })
                        } catch (caught) {
                          toast({
                            variant: 'destructive',
                            title: tt('Nepodarilo sa priradiť obsluhu.'),
                            description: apiErrorMessage(caught, ''),
                          })
                          return
                        }
                        try {
                          // The reason is the waiter just picked - the datum this dialog exists to
                          // collect, not a default invented to satisfy `@NotBlank`.
                          await postOrdersOrderIdConfirmStatus(target.id ?? 0, {
                            newStatus: 'delivered',
                            reason: closeReasonFor(waiter),
                          })
                          refresh()
                        } catch (caught) {
                          // The missing `onError` was the defect underneath the defect, and the
                          // reason a silent failure survived a day of green runs.
                          toast({
                            variant: 'destructive',
                            title: tt('Obsluha je priradená, ale objednávku sa nepodarilo uzavrieť.'),
                            description: isForbidden(caught)
                              ? statusRoleNote('delivered')
                              : apiErrorMessage(caught, ''),
                          })
                          refresh()
                        }
                      })()
                    }}
                    className="w-full min-h-[44px] rounded-xl border border-border text-sm font-semibold hover:bg-muted"
                  >
                    {waiter.name}
                  </button>
                ))}
              </div>
            )}
            {/* §9.488 - **this dialog had no dismiss control inside it at all**, only the scrim outside, and
                adding the trap without one would have made it worse: the opening focus would land on the
                first waiter and a reflexive Enter assigns the takings to the wrong person. That is §9.477's
                own argument - *"one that opened with Odstrániť focused would turn a reflexive Enter into a
                deletion"* - with an attribution in place of a deletion. So the safe action takes the focus
                and every waiter is one Tab away. */}
            <button
              type="button"
              data-dialog-cancel
              onClick={() => setAskingWho(null)}
              className="w-full min-h-[40px] rounded-xl border border-border text-xs font-semibold text-muted-foreground hover:bg-muted"
            >
              <T>Zrušiť</T>
            </button>
          </section>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((placeholder) => (
            <div
              key={placeholder}
              className="h-20 rounded-2xl bg-muted/40 border border-border animate-pulse"
            />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<Truck className="w-10 h-10" />}
            title={tt('Žiadne objednávky')}
            description={tt('Nástenka je prázdna.')}
          />
        </div>
      ) : filterStatus === 'active' ? (
        /* The nástenka proper — §9.104. Grouped as legacy groups it, because a dispatcher picking one
         * status at a time can never see the road at once, and seeing the road at once is the point. */
        <div className="space-y-5" data-board-bands>
          {boardBands(shown).map((band) => (
            <div key={band.status} className="space-y-2" data-board-band={band.status}>
              <div
                className={cn(
                  'flex items-center gap-2 rounded-xl px-3 py-2 border',
                  /* §9.643, owner O34 - §9.318's dashed amber band is gone, because a not-yet-due preorder is
                     no longer on this board at all. Every band here is now today's work, so there is nothing
                     left to mark as not being it. */
                  'border-border bg-muted/30',
                )}
              >
                <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', band.dot)} />
                <span className="text-xs font-black uppercase tracking-wider">{band.label}</span>
                <span className="ml-auto text-xs font-bold opacity-70" data-band-count>
                  {band.orders.length}
                </span>
              </div>
              <div className="space-y-2 pl-1">
                {band.orders.map((order) => (
                  <DispatchRow
                    key={order.id}
                    order={order}
                    clock={now}
                    drivers={drivers}
                    isSelected={selected.has(order.id ?? 0)}
                    canChangeStatus={hasPermission('change_order_status')}
                    canCancel={hasPermission('cancel_order')}
                    canAccept={mayAccept}
                    canEdit={mayEditOrder}
                    mayDispatch={mayDispatch}
                    driven={usesDrivers(order.type ?? '', channels)}
                    channelOffers={offersToCouriers(order.type ?? '', channels)}
                    channels={channels}
                    onToggleSelect={() => toggleSelected(order.id ?? 0)}
                    onAssignDriver={(driverId) => assignDriver(order, driverId)}
                    onRemoveDriver={() => removeDriver(order)}
                    onStatusChange={(status) => changeStatus(order, status)}
                    onDispatchOne={() => void dispatchOne(order)}
                    onEdit={() => navigate(`/new-order?edit=${order.id ?? 0}`)}
                    onStornoed={refresh}
                    couriers={couriers}
                    mayOffer={mayOffer}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Every other tab is already one status — a band round the whole list says nothing. */
        <div className="space-y-2">
          {shown.map((order) => (
            <DispatchRow
              key={order.id}
              order={order}
              clock={now}
              drivers={drivers}
              isSelected={selected.has(order.id ?? 0)}
              canChangeStatus={hasPermission('change_order_status')}
              canCancel={hasPermission('cancel_order')}
              canAccept={mayAccept}
              canEdit={mayEditOrder}
              mayDispatch={mayDispatch}
              driven={usesDrivers(order.type ?? '', channels)}
              channelOffers={offersToCouriers(order.type ?? '', channels)}
              channels={channels}
              onToggleSelect={() => toggleSelected(order.id ?? 0)}
              onAssignDriver={(driverId) => assignDriver(order, driverId)}
              onRemoveDriver={() => removeDriver(order)}
              onStatusChange={(status) => changeStatus(order, status)}
              onDispatchOne={() => void dispatchOne(order)}
              onEdit={() => navigate(`/new-order?edit=${order.id ?? 0}`)}
              onStornoed={refresh}
              couriers={couriers}
              mayOffer={mayOffer}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function DispatchRow({
  order,
  clock,
  drivers,
  isSelected,
  canChangeStatus,
  canCancel,
  canAccept,
  canEdit,
  mayDispatch,
  driven,
  channelOffers,
  channels,
  onToggleSelect,
  onAssignDriver,
  onRemoveDriver,
  onStatusChange,
  onDispatchOne,
  onEdit,
  onStornoed,
  couriers,
  mayOffer,
}: {
  order: BoardOrder
  clock: Date
  drivers: DriverLike[]
  isSelected: boolean
  canChangeStatus: boolean
  canCancel: boolean
  /** §9.744 - `accept_order`, the right *✓ Prijať* asks for (BACKEND.md §10A.397). */
  canAccept: boolean
  /** §9.744 - `edit_order` and `new_order`, the rights *Upraviť* asks for (`mayOpenOrderEditor`). */
  canEdit: boolean
  /** §9.728 - `dispatch_order`, the right *Vyraziť* asks for. */
  mayDispatch: boolean
  driven: boolean
  /** §9.792, owner O144 - the order's channel lets the board offer it to external couriers (Predaj a účtovanie). */
  channelOffers: boolean
  /** §9.391 - the row asks the channel whether this order is one somebody serves. */
  channels: readonly { slug?: string; onPremise?: boolean }[]
  onToggleSelect: () => void
  onAssignDriver: (driverId: number) => void
  /** §9.812, owner O155 - *Odstrániť* beside the driver's name. */
  onRemoveDriver: () => void
  onStatusChange: (status: string) => void
  onDispatchOne: () => void
  onEdit: () => void
  /** §9.442 - the board re-reads after a reversal so the card stops offering it. */
  onStornoed: () => void
  /** §9.786 - the franchise has the external couriers' module. */
  couriers: boolean
  /** §9.786 - the board's own right, which *Ponúknuť externým* asks for. */
  mayOffer: boolean
}) {
  const TypeIcon = TYPE_ICONS[order.type ?? ''] ?? Truck
  const sourceOf = sourceBadge(order)
  const approval = paymentApprovalBadge(order.paymentMethod, order.paymentStatus)
  /* §9.744 - each button on its own right: *✓ Prijať* on `accept_order`, *Stornovať* on `cancel_order`, the rest on
     `change_order_status`. One rule beside its cases, so the card cannot ask a second way. */
  const transitions = transitionsFor(order.status, {
    accept: canAccept,
    changeStatus: canChangeStatus,
    cancel: canCancel,
  })
  const dispatchButton = dispatchButtonFor(order, mayDispatch)
  const driverLocked = !canReassignDriver(order)
  // §9.323 - local to the card, and reset by the assignment itself below. Kept out of
  // `driverControlFor` because it is not a rule about the order; it is whether somebody has asked.
  const [reassigning, setReassigning] = useState(false)
  // §9.309, the owner's item B - what belongs where the picker used to be, unconditionally.
  const driverControl = driverControlFor(order, { driven, mayChange: canChangeStatus })
  // §9.812, owner O155 - the same lock as changing the driver, on the same right.
  const removable = mayRemoveDriver(order, { mayChange: canChangeStatus })
  // **The order's own name first, and that ordering is the point.** `GET /employees/drivers` answers
  // only drivers seen in the **last four hours**, so looking the name up there alone rendered an
  // empty badge for exactly the case this badge exists for: an order on the road whose driver has
  // since gone off duty. Caught by the e2e, which stubs `on_the_way` with a driver who is not on the
  // duty list. The id is the last resort, because *Šofér #7* is still more use than nothing.
  const driverName =
    order.driverName ??
    drivers.find((driver) => driver.id === order.driverId)?.name ??
    (order.driverId ? `${tt('Šofér')} #${order.driverId}` : '')
  const closesHere = canChangeStatus && mayCloseFromBoard(order, channels)
  // §9.786 - whether this card offers it to the external couriers, takes the offer back, or says why not.
  const offerAction = offerActionFor(order, { couriers, driven, mayOffer, channelOffers })
  const payment = paymentBadge(order)
  const phoneSeen = verificationOf(order.phoneOrderCount)
  // §9.154 — everything legacy's row says. `now` is taken once per render rather than inside each
  // helper, so the clock on one row cannot disagree with the clock on the row beneath it.
  const now = clock.getTime()
  const channel = channelBadge(order.type)
  const method = paymentMethodBadge(order.paymentMethod)
  // §9.204 - the same two icons the payment dialog uses, so the board and the till agree; §9.741 - and the QR
  // icon the badge names for a door QR payment, which drew a card while every label already said *QR Pay*.
  const MethodIcon = method?.icon === 'cash' ? Banknote : method?.icon === 'qr' ? QrCode : CreditCard
  const arrived = arrivalLabel(order.createdAt, now)
  const waiting = elapsedLabel(order.createdAt, now)
  const onRoad = onRoadLabel(order, now)
  const discount = discountParts(order)
  const promised = scheduledBadge(order, now)
  const emailSeen = verificationOf(order.emailOrderCount)

  return (
    <div
      data-order={order.orderNumber}
      data-order-status={order.status ?? ''}
      className={cn(
        // §9.149 — the tint is what makes a status readable without reading the word.
        // §9.204 — shorter, so more orders fit under one another. The tints and the strip are
        // untouched: *farby zachovať* was explicit, and §9.149 records why they carry the status.
        'relative overflow-hidden border rounded-2xl pl-5 pr-4 py-2 flex flex-wrap items-center gap-2',
        statusTint(order.status),
        isSelected ? 'border-primary ring-1 ring-primary/30' : 'border-border',
      )}
    >
      {/* Legacy's left strip, the width it uses. */}
      <span
        aria-hidden
        data-status-strip={order.status ?? ''}
        className={cn('absolute left-0 top-0 bottom-0 w-1.5', statusStrip(order.status))}
      />
      <button
        type="button"
        aria-label={`${tt('Vybrať')} #${order.orderNumber}`}
        onClick={onToggleSelect}
        className={cn(
          'w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors',
          // §9.417, report 3 - *"Checkbox button for selecting is so invisible, make it darker."*
          // It was a one-pixel `border-border` outline with **no background**, on a card of the same
          // colour: nothing to see until it was ticked. A filled ground and a two-pixel border make
          // an unticked box visible, which matters because report 1 is *"I had selected 15903"* and a
          // selection nobody can see is a selection nobody can check.
          isSelected
            ? 'bg-primary border-primary text-primary-foreground'
            : 'bg-muted border-muted-foreground/50 hover:border-muted-foreground',
        )}
      >
        {isSelected && <Check className="w-3.5 h-3.5" />}
      </button>

      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* §9.204 - the thing a driver and a dispatcher say out loud to each other. It was
              `text-xs`: the size of the badges beside it and smaller than the customer name. */}
          <span className="font-mono text-xl font-black text-primary leading-none" data-order-number>
            #{order.orderNumber}
          </span>
          {/* §9.154 - legacy names the channel beside the icon. A truck and a bag are the same
            * silhouette at a glance, and *Osobný odber* is the one no driver should be assigned to. */}
          {channel !== null && (
            <span
              data-order-channel={order.type}
              className={cn(
                'flex items-center gap-0.5 text-[10px] font-bold rounded px-1 py-0.5 border leading-none',
                channel.tone,
              )}
            >
              <TypeIcon className="w-2.5 h-2.5" />
              {channel.label}
            </span>
          )}
          {/* §9.417, FB-744 report 4 - where the order came from. A missed port: legacy draws both
            * (`dispatch.tsx:482`) and `source` has been on the payload and on `BoardOrder` all
            * along. */}
          {sourceOf !== null && (
            <span
              data-order-source={order.source}
              className={cn(
                'flex items-center gap-0.5 text-[10px] font-bold rounded px-1 py-0.5 border leading-none',
                sourceOf.tone,
              )}
            >
              {order.source === 'web' ? (
                <Globe className="w-2.5 h-2.5" />
              ) : (
                <Phone className="w-2.5 h-2.5" />
              )}
              {sourceOf.label}
            </span>
          )}
          {/* §9.417, reports 4 and 6 - whether the money is confirmed, for a card paid online.
            * Amber for waiting rather than legacy's red, which the owner asked for and is right
            * about: red says failed. */}
          {approval !== null && (
            <span
              data-payment-approval={order.paymentStatus ?? 'unknown'}
              className={cn(
                'text-[10px] font-bold rounded px-1 py-0.5 border leading-none whitespace-nowrap',
                approval.tone,
              )}
            >
              {approval.label}
            </span>
          )}
          {method !== null && (
            <span
              data-payment-method={order.paymentMethod}
              className={cn(
                'flex items-center gap-0.5 text-[10px] font-bold rounded px-1 py-0.5 border leading-none',
                method.tone,
              )}
            >
              {/* §9.204 - §9.154 gave the channel an icon because *a truck and a bag are the same
                  silhouette at a glance*; cash and card are the two states somebody scans a whole
                  column for. The same two icons the payment dialog uses, so board and till agree. */}
              <MethodIcon className="w-2.5 h-2.5" />
              {method.label}
            </span>
          )}
          {/* §9.786 - waiting for an external courier, or which one took it. */}
          <CourierStateBadge order={order} />
        </div>
        {/* §9.125 - who the bag is for. This board decides which car it goes in and showed only the
          * driver, so the name, the number and the address were on every other screen but this one. */}
        <p
          className="text-sm font-semibold truncate"
          data-customer-name={customerLabel(order)}
          // §9.429 - the same one attribute. A clipped name is who the dispatcher is about to ring.
          title={customerTitle(order)}
        >
          {/* §9.437 - this used to read `order.customerName ?? order.driverName ?? '-'`, so an order
              with no customer name printed the **driver's** name in the customer's place. The
              comment above says §9.125 added this line *because* the board showed only the driver;
              the fallback put it back. `customerLabel` treats a blank like a null, which is the
              shape a till saves when nobody fills the box in. */}
          {customerLabel(order)}
        </p>
        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
          {order.customerPhone && (
            <a
              href={`tel:${order.customerPhone}`}
              // §9.381 - outside the app, like every other external hand-off. This board is read on
              // a phone too, and a `tel:` in this document unloads it.
              target="_blank"
              rel="noreferrer"
              data-customer-phone
              onClick={(event) => event.stopPropagation()}
              className={cn(
                'flex items-center gap-1 font-medium hover:underline',
                phoneSeen.known ? 'text-emerald-400' : 'text-foreground',
              )}
            >
              <Phone className="w-3 h-3" />
              {order.customerPhone}
              <span
                data-phone-verified={phoneSeen.known ? 'yes' : 'no'}
                className={cn(
                  'text-[9px] font-black px-1.5 py-px rounded-full border',
                  phoneSeen.known
                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                    : 'bg-red-500/15 text-red-400 border-red-500/30',
                )}
              >
                {phoneSeen.label}
              </span>
            </a>
          )}
          {/* §9.154 - legacy shows the address the order was placed from *and* the e-mail it was
            * placed with, each carrying how often that contact has ordered before. */}
          {order.customerEmail && (
            <span
              data-customer-email
              className={cn(
                'flex items-center gap-1 truncate',
                emailSeen.known ? 'text-emerald-400' : 'text-foreground',
              )}
            >
              ✉ {order.customerEmail}
              <span
                data-email-verified={emailSeen.known ? 'yes' : 'no'}
                className={cn(
                  'text-[9px] font-black px-1.5 py-px rounded-full border',
                  emailSeen.known
                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                    : 'bg-red-500/15 text-red-400 border-red-500/30',
                )}
              >
                {emailSeen.label}
              </span>
            </span>
          )}
          {order.customerAddress && (
            // §9.204 — the line a driver needs most, and it was the palest thing on the card. The
            // icon stays muted: it is decoration, the address is the content.
            <span
              className="flex items-center gap-1 text-foreground truncate"
              data-customer-address
              /* §9.429 - **reachable, since the card has a width.** §9.204 called this *the line a
               * driver needs most* and fixed its colour; it stayed `truncate` with no way to read
               * the rest, so a long address is the one thing this board exists to convey and cannot.
               *
               * A `title` rather than `break-words`: unlike the note beside it, an address is one
               * line of a dense card in a column, and unclipping it would push every other row about
               * to serve the rare long one. The same call as the stock movement note in §9.428, for
               * the same reason - unclip in a card, make reachable in a constrained row. */
              title={order.customerAddress}
            >
              <MapPin className="w-3 h-3 text-muted-foreground" />
              {order.customerAddress}
            </span>
          )}
          {outsideEveryZone(order) && (
            // §9.351 - the server resolved no delivery zone for these coordinates, which is 147 of
            // the owner's orders and 104 of them in Šurany itself. **A marker, not a warning**:
            // §10A.139 accepts every one of them on purpose, so this tells whoever is assigning a
            // driver and does not colour the row or block anything. It sits beside the address
            // because that is the thing it is about.
            <span
              className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground"
              data-outside-zone
              title={tt('Adresa nie je v žiadnej doručovacej zóne - cena dovozu sa neurčila automaticky')}
            >
              <MapPin className="w-3 h-3" />
              <T>mimo zóny</T>
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap mt-0.5">
          {payment !== null && (
            <span
              // §9.154 — `unpaid` rather than nothing when the server sent no status: React drops an
              // attribute whose value is undefined, so the badge that says *čaká* on an unpaid
              // CardPay order had no handle at all, and the very case this exists for was untestable.
              data-payment-state={order.paymentStatus ?? 'unpaid'}
              className={cn('text-[10px] font-bold rounded px-1 py-0.5 border leading-none', payment.tone)}
            >
              {payment.label}
            </span>
          )}
          {order.isEdited === true && (
            <span
              data-order-edited
              title={tt('Objednávka bola upravená')}
              className="text-[11px] font-black text-red-500 bg-red-500/15 border border-red-500/30 rounded px-1 py-0.5 leading-none"
            >
              !
            </span>
          )}
        </div>
        {/* §9.154 - a pre-order's promised time and the countdown to it. Three tones for three
          * situations: overdue says by how much, within the hour is urgent, further out is calm. */}
        {promised !== null && (
          <div
            data-scheduled-state={promised.state}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-2 py-1 w-fit text-[11px] mt-0.5 border',
              promised.state === 'late'
                ? 'bg-red-500/15 border-red-500/30 text-red-400'
                : promised.state === 'urgent'
                  ? 'bg-amber-500/15 border-amber-500/30 text-amber-500'
                  : 'bg-amber-500/10 border-amber-500/20 text-amber-400',
            )}
          >
            <CalendarClock className="w-3 h-3 flex-shrink-0" />
            <span className="font-semibold">
              <T>Doručenie:</T>
            </span>
            <span className="font-black">{promised.promisedAt}</span>
            <span className="font-bold tabular-nums">{promised.countdown}</span>
          </div>
        )}

        {/* §9.54 - the note. `/orders` has always carried it and this board never drew it, so a
          * *zvoňte na susedov* reached the kitchen and the driver's own screen and not the person
          * assigning the driver. Nobody typing a note knows who it is for. */}
        {/* §9.425, the owner: *"can you make that NOTE in the order will be.. much better visible?"*
          *
          * It was `text-[11px] text-muted-foreground truncate` - **the quietest thing on the row,
          * and cut off.** A note is the one field on a delivery whose whole purpose is that somebody
          * reads it: *zvoňte na susedov* is an instruction, and §9.54 added it here precisely
          * because the person assigning the driver never saw it. Drawing it in grey at 11px and then
          * truncating it is that defect at one remove.
          *
          * Amber and bordered, because the board's other colours are already spoken for - emerald
          * for a payment that cleared, red for one that failed, zinc for status - and a note is
          * neither good news nor bad.
          *
          * **`break-words` rather than `truncate`.** A long note makes the row taller, and that is
          * the right trade: the row exists to be read. The `title` stays for the hover. */}
        {order.notes && (
          <p
            className="flex items-start gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-600 dark:text-amber-400"
            data-order-note
            title={order.notes}
          >
            <StickyNote className="w-3.5 h-3.5 shrink-0 mt-px" />
            <span className="min-w-0 break-words">{order.notes}</span>
          </p>
        )}
      </div>

      {/* §9.149 - was `label={order.status}`: the raw key, in grey, on every row. */}
      <StatusBadge color="zinc" label={orderStatusLabel(order.status)} />

      {/* §9.154 - the answer to *what has been waiting longest*, which this board existed to give
        * and could not be asked. Legacy's own format: seconds, then minutes, then `Nh Mm`. */}
      {arrived !== null && (
        <span
          data-order-arrived
          title={`${tt('Prijatá:')} ${order.createdAt ?? ''}`}
          className="flex items-center gap-1 text-[11px] text-muted-foreground whitespace-nowrap"
        >
          <Clock className="w-3 h-3" />
          <span className="font-semibold text-foreground">{arrived}</span>
          {waiting !== null && <span data-order-waiting className="text-muted-foreground/60">({waiting})</span>}
        </span>
      )}

      {/* §9.438 - how long the van has been out, which is a different question from the one above
        * and the one a dispatcher has about this band. *Accepted 47 minutes ago* is true whether it
        * left two minutes ago or twenty-five, and those are opposite decisions: one is fine, the
        * other is a phone call. `onTheWayAt` has been on `OrderListItem` all along - the trip log
        * measures the journey from it - and no screen read it. Beside the figure above rather than
        * instead of it, so *what has been waiting longest* still answers.
        *
        * **No word beside the figure, and that was a correction.** It first read *Na ceste 25 min*,
        * which is the status badge's own label repeated two elements later - the card would have
        * said it twice. The truck distinguishes it from the clock on the figure beside it, which
        * carries no word either, and the tooltip is a whole sentence because two bare durations in
        * one row need one. Caught by reading the rendered row; no test asserts that a card does not
        * say the same thing twice. */}
      {onRoad !== null && (
        <span
          data-order-on-road={onRoad}
          title={`${tt('Vyrazil o')} ${order.onTheWayAt ?? ''} - ${tt('na ceste')} ${onRoad}`}
          className="flex items-center gap-1 text-[11px] whitespace-nowrap text-violet-300"
        >
          <Truck className="w-3 h-3" />
          <span className="font-semibold">{onRoad}</span>
        </span>
      )}

      {/* §9.154 - what the bill was before the discount, which legacy shows and we hid. */}
      <span className="flex items-center gap-1 whitespace-nowrap">
        {discount !== null && (
          <>
            <span data-order-original className="text-xs text-muted-foreground line-through">
              {formatMoney(discount.original)}
            </span>
            <span data-order-discount className="text-xs font-semibold text-amber-400">
              −{formatMoney(discount.discount)}
            </span>
            <span className="text-muted-foreground/60 text-xs">=</span>
          </>
        )}
        <span className="font-bold tabular-nums">{formatMoney(order.total)}</span>
      </span>

      <div className="flex-1" />

      {/* §9.309, owner item B - *"driver assignment on every order feels redundant"*. It was on every
        * card: on an order the kitchen had not started, on one already on the road as a dead disabled
        * control, and on a **counter collection**, which nobody drives. `driverControlFor` decides,
        * and `name` is the branch that keeps this safe — narrowing a control must not hide who has
        * the food. */}
      {/* §9.323 - `'reassign'` shows the name and reveals this picker when asked, so an order
          already on its way does not carry the widest control on the card over its driver's name. */}
      {(driverControl === 'assign' || (driverControl === 'reassign' && reassigning)) && (
        <select
          value={order.driverId ? String(order.driverId) : ''}
          onChange={(event) => {
            const value = Number(event.target.value)
            if (value) onAssignDriver(value)
            setReassigning(false)
          }}
          disabled={driverLocked}
          data-driver-picker={order.orderNumber}
          aria-label={`${tt('Šofér')} #${order.orderNumber}`}
          title={driverLocked ? tt('Objednávku musí vrátiť šofér cez mobilnú aplikáciu.') : undefined}
          className="px-2.5 py-1.5 rounded-lg bg-background border border-border text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">{tt('- šofér -')}</option>
          {drivers.map((driver) => (
            <option key={driver.id} value={driver.id}>
              {driver.name}
            </option>
          ))}
        </select>
      )}

      {driverControl === 'reassign' && !reassigning && (
        <span
          data-driver-assigned={order.orderNumber}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted/40 border border-border text-xs font-semibold"
        >
          <UserCheck className="w-3 h-3 text-muted-foreground" />
          <T>{driverName}</T>
          {/* A reveal and not a dialog. The option the owner rejected was *"fast during a rush and
              easy to do by accident"*; a confirmation would trade the second problem for the
              first, so fixing a mistake is still one click away. */}
          <button
            type="button"
            data-driver-change={order.orderNumber}
            onClick={() => setReassigning(true)}
            className="ml-0.5 text-[10px] font-bold uppercase tracking-wide text-primary"
          >
            <T>Zmeniť</T>
          </button>
          {removable && (
            <button
              type="button"
              data-driver-remove={order.orderNumber}
              onClick={onRemoveDriver}
              className="ml-0.5 text-[10px] font-bold uppercase tracking-wide text-destructive"
            >
              <T>Odstrániť</T>
            </button>
          )}
        </span>
      )}

      {driverControl === 'name' && (
        <span
          data-driver-name={order.orderNumber}
          title={
            driverLocked ? tt('Objednávku musí vrátiť šofér cez mobilnú aplikáciu.') : undefined
          }
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted/40 border border-border text-xs font-semibold"
        >
          <UserCheck className="w-3 h-3 text-muted-foreground" />
          {/* A name from the employee list, not a label - it goes through `T` like every other
            * person's name on this board does. */}
          <T>{driverName}</T>
          {/* §9.812 - a driver pre-assigned to an order the kitchen is still making can come off it too. */}
          {removable && (
            <button
              type="button"
              data-driver-remove={order.orderNumber}
              onClick={onRemoveDriver}
              className="ml-0.5 text-[10px] font-bold uppercase tracking-wide text-destructive"
            >
              <T>Odstrániť</T>
            </button>
          )}
        </span>
      )}

      {/* §9.786, owner O139 - *Ponúknuť externým* / *Stiahnuť ponuku*, or why it cannot go with one. The board re-reads
        * after either, so the badge follows. */}
      <CourierOfferAction order={order} action={offerAction} onDone={onStornoed} />

      {/* §9.744 - `edit_order` and `new_order`, where §9.124 had asked `change_order_status`; the order's own rule is
        * unchanged. */}
      {editButtonFor(order, canEdit, channels) && (
        <button
          type="button"
          data-edit-order
          onClick={onEdit}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border bg-amber-500/10 text-amber-500 border-amber-500/30 hover:bg-amber-500/20 transition-colors"
        >
          <Pencil className="w-3 h-3" /> <T>Upraviť</T>
        </button>
      )}

      {/* §9.442, FB-761, owner question #39 answered **both** - so the card carries it as well as
        * the toolbar. Per order it cannot reverse more than the operator pointed at, which is why
        * it is here and not only in the selection. Renders nothing unless a document exists and is
        * unreversed. */}
      <FiskalStornoAction
        order={order}
        onDone={onStornoed}
        compact
        label={tt('Storno fiškál')}
        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border bg-red-500/10 text-red-500 border-red-500/30 hover:bg-red-500/20 transition-colors"
      />

      {closesHere && (
        <button
          type="button"
          data-close-in-house
          onClick={() => onStatusChange('delivered')}
          // §9.405 - **blue, and green moved to *Prijať*.** The owner: *"make vydaná as blue
          // colored and Prijať as green color buttons."* It is the right way round on its own
          // merits: an unaccepted web order is the one state on this board where a customer is
          // waiting and nothing is happening, and *Vydaná* is bookkeeping after the food has gone.
          className="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-bold hover:bg-blue-400 transition-colors"
        >
          <T>Vydaná</T>
        </button>
      )}

      {/* §9.417, report 5 - *Vyraziť* is drawn whenever a driver is assigned, disabled when the
        * order is not ready for it, and absent when nobody is assigned. It used to appear and
        * disappear with the transition, so it moved around the card. */}
      {dispatchButton.shown && (
        <button
          type="button"
          data-dispatch-one
          disabled={!dispatchButton.enabled}
          /* §9.728 - the reason comes with the state: the right's name without it, the status otherwise. */
          title={dispatchButton.reason ?? undefined}
          onClick={onDispatchOne}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-500"
        >
          <Send className="w-3 h-3" /> <T>Vyraziť</T>
        </button>
      )}

      {transitions.filter((next) => next !== 'on_the_way').map((next) => (
        <button
          key={next}
          type="button"
          // §9.341 - the transition buttons carried no attribute, so nothing could drive a storno
          // from a test. The status the button sets is the one thing a test needs to name.
          data-status-next={next}
          onClick={() => onStatusChange(next)}
          className={cn(
            'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-colors',
            next === 'cancelled'
              ? 'bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20'
              : // §9.405 - *✓ Prijať* is the transition to `new`, and it is the one act on this
                // board that takes responsibility for an order a customer is already waiting on.
                // Solid rather than tinted, like *Vydaná*: a tint at 10 % reads as the same button
                // as *→ V príprave* beside it.
                next === 'new'
                ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-400'
                : 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20',
          )}
        >
          {next === 'cancelled' && <Ban className="w-3 h-3" />}
          {nextStatusLabel(order.status, next)}
        </button>
      ))}
    </div>
  )
}
